import { DateTime } from '@ksyos/date-time';
import { Button, Card, Form, FormButtonRow, FormGroup, FormParagraph, IconButton } from '@ksyos/design-system';
import { pick } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IKsyosAccount } from '@common/interfaces/ksyosAccount';
import type { IEmployment } from '@frontend/components/interfaces/employment';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization, ToastType, ValidationErrorCode } from '@common/enums';
import { ksyosAccountSchema } from '@common/validation/practitioner';
import { FormContextProvider } from '@frontend/components/elements/formContext';
import { EndEmploymentsModal } from '@frontend/components/elements/modals/EndEmploymentsModal';
import { StopRightsModal } from '@frontend/components/elements/StopRightsModal';
import { CancelButton } from '@frontend/components/userDataManagement/CancelButton';
import { InputTextField } from '@frontend/components/userDataManagement/InputText';
import { SaveButton } from '@frontend/components/userDataManagement/SaveButton';
import { translateKey } from '@frontend/translate';
import {
    emptyValidationErrors,
    hasOneAuthorization,
    showToast,
    updateValidationErrorsForOne,
    useAbortableFetch,
    useValidationErrors,
    validate,
} from '@frontend/utils/';

export interface IKsyosAccountProps {
    practitionerId: string;
}

interface IEmploymentDates {
    endDate?: Date | string;
    startDate?: Date | string;
}

type EditableKsyosAccount = Pick<IKsyosAccount, 'emailAddress' | 'userName' | 'isActivated'>;

export function KsyosAccount(props: IKsyosAccountProps) {
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const [originalKsyosAccount, setOriginalKsyosAccount] = useState<IKsyosAccount>();
    const [ksyosAccount, setKsyosAccount] = useState<IKsyosAccount>();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [hasActiveEmployments, setHasActiveEmployments] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showEndEmploymentsModal, setShowEndEmploymentsModal] = useState(false);
    const [errors, setErrors] = useValidationErrors();
    const [employmentPeriod, setEmploymentPeriod] = useState<IEmploymentDates>();
    const abortableFetch = useAbortableFetch();
    const endDate = DateTime.now();

    useEffect(() => {
        loadKsyosAccount();
    }, [props.practitionerId]);

    if (!ksyosAccount) {
        return null;
    }

    return (
        <FormContextProvider
            value={{
                placeholderGroupName: 'ksyosAccount',
                item: ksyosAccount,
                errors: errors,
                onChange: async (key, value) => await handleChange(key, value),
                onDoValidate: async (key) => await validateOne(key),
            }}
        >
            <Card>
                <Card.Transition type={ksyosAccount.authorization.canUpdate && isEditing ? 'edit' : 'read'} />
                <Card.Header
                    action={
                        ksyosAccount.authorization.canUpdate && !isEditing ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : undefined
                    }
                    title={translateKey('profile.ksyosAccount')}
                />
                <Card.Body>
                    <Form submitOnEnter={false}>
                        {renderTextInput('userName', 'common.userName', ksyosAccount.userName)}

                        <FormGroup label={translateKey('common.userNumber')}>
                            <FormParagraph>{ksyosAccount.uid ? ksyosAccount.uid : '-'}</FormParagraph>
                        </FormGroup>

                        {ksyosAccount.isGenerated !== undefined && (
                            <FormGroup label={translateKey('common.isGenerated')}>
                                <FormParagraph>
                                    {ksyosAccount.isGenerated ? translateKey('common.yes') : translateKey('common.no')}
                                </FormParagraph>
                            </FormGroup>
                        )}

                        {renderTextInput('emailAddress', 'common.emailAddress', ksyosAccount.emailAddress)}

                        {isManager && (
                            <>
                                {renderIsActivated()}
                                <FormGroup label={translateKey('profile.employmentPeriod')}>
                                    {(employmentPeriod?.startDate || employmentPeriod?.endDate) && (
                                        <FormParagraph>
                                            {getStartAndEndDateFormatter(
                                                employmentPeriod?.startDate,
                                                employmentPeriod?.endDate
                                            )}
                                        </FormParagraph>
                                    )}
                                </FormGroup>
                            </>
                        )}

                        {isEditing && (
                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={async () => await handleCancel()}
                                />
                                {hasActiveEmployments &&
                                    hasOneAuthorization([
                                        ManagerAuthorization.practitionerAddEdit,
                                        ManagerAuthorization.adminsAddEdit,
                                    ]) && (
                                        <Button
                                            isDisabled={isSaving}
                                            onClick={() => setShowEndEmploymentsModal(true)}
                                            type="button"
                                            variant="outline"
                                        >
                                            {translateKey('employment.endAll')}
                                        </Button>
                                    )}
                                <SaveButton
                                    disabled={isSaving}
                                    onSave={async () => await handleSubmit()}
                                />
                            </FormButtonRow>
                        )}
                    </Form>
                </Card.Body>
            </Card>

            {hasOneAuthorization([ManagerAuthorization.adminsAddEdit, ManagerAuthorization.practitionerAddEdit]) && (
                <EndEmploymentsModal
                    backUrl={`/practitioners/${ksyosAccount?.id}/profile`}
                    handleNo={() => setShowEndEmploymentsModal(false)}
                    isOpen={showEndEmploymentsModal}
                    practitionerId={ksyosAccount?.id}
                />
            )}

            {showConfirmationModal && (
                <StopRightsModal
                    alertMessages={[translateKey('serviceRightStopModal.warningEmployment')]}
                    bodyText={translateKey('serviceRightStopModal.bodyEmployment')}
                    confirmText={translateKey('profile.deactivate')}
                    endDate={endDate.format('DD-MM-YYYY')}
                    onClose={async () => setShowConfirmationModal(false)}
                    onStopRight={async () => {
                        handleSubmit();
                    }}
                    title={translateKey('serviceRightStopModal.titleEmployment')}
                />
            )}
        </FormContextProvider>
    );

    function renderTextInput<K extends keyof EditableKsyosAccount>(fieldKey: K, label: string, value: string | null) {
        if (isEditable(fieldKey)) {
            return (
                <InputTextField
                    dontValidateOnBlur
                    error={errors.get(fieldKey)}
                    fieldKey={fieldKey}
                    hasErrors={errors.has(fieldKey)}
                    isRequired
                    label={label}
                />
            );
        } else {
            return (
                <FormGroup label={translateKey(label)}>
                    <FormParagraph>{value ? value : '-'}</FormParagraph>
                </FormGroup>
            );
        }
    }

    function renderIsActivated() {
        if (ksyosAccount?.isActivated === undefined) {
            return;
        }

        return (
            <FormGroup label={translateKey('profile.accountActivated')}>
                <FormParagraph>
                    {ksyosAccount.isActivated ? translateKey('common.yes') : translateKey('common.no')}
                </FormParagraph>
            </FormGroup>
        );
    }

    function isEditable<K extends keyof EditableKsyosAccount>(fieldKey: K) {
        return (
            ksyosAccount?.authorization.canUpdate &&
            isEditing &&
            ksyosAccount?.authorization.canUpdateProperties?.includes(fieldKey)
        );
    }

    function getStartAndEndDateFormatter(
        employmentStartDate: string | Date | undefined,
        employmentEndDate: string | Date | undefined
    ) {
        let startDateFormatted = 'vanaf';
        let endDateFormatted = 'heden';

        if (employmentStartDate && employmentStartDate !== 'vanaf') {
            startDateFormatted = DateTime.from(employmentStartDate).format('DD-MM-YYYY');
        }
        if (employmentEndDate && employmentEndDate !== 'heden') {
            endDateFormatted = DateTime.from(employmentEndDate).format('DD-MM-YYYY');
        }

        return `${startDateFormatted} - ${endDateFormatted}`;
    }

    async function handleChange<K extends keyof IKsyosAccount>(name: K, value: IKsyosAccount[K]) {
        setKsyosAccount({ ...ksyosAccount, [name]: value } as IKsyosAccount);
    }

    async function validateOne<K extends keyof IKsyosAccount>(name: K) {
        setErrors(updateValidationErrorsForOne(ksyosAccountSchema, ksyosAccount, errors, name));
    }

    async function loadKsyosAccount() {
        const url = `/api/practitioners/${props.practitionerId}/ksyosAccount?includeItemAuthorizations=canUpdate,canUpdateProperties`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        const activeEmployments = (
            await abortableFetch.fetchJson(`/api/user/employments?practitionerId=${result.json?.id}`, 'GET')
        ).json.items;

        setOriginalKsyosAccount(result.json);
        setKsyosAccount(result.json);
        await getEmploymentsInformation();
        setHasActiveEmployments(
            activeEmployments.length > 0 &&
                activeEmployments.every(
                    (employment: IEmployment) =>
                        !employment.endDate || DateTime.from(employment.endDate).isAfter(DateTime.now())
                )
        );
    }

    async function handleCancel() {
        loadKsyosAccount();
        setErrors(emptyValidationErrors);
        setIsEditing(false);
    }

    async function handleSubmit() {
        if (!ksyosAccount || !originalKsyosAccount) {
            return;
        }

        if (!showConfirmationModal && originalKsyosAccount.isActivated && !ksyosAccount.isActivated) {
            setShowConfirmationModal(true);
        } else {
            setIsSaving(true);

            const filteredKsyosAccount = {
                authorization: { ...ksyosAccount.authorization },
                ...pick(ksyosAccount, ksyosAccount.authorization.canUpdateProperties!),
            };
            const { errors: validationErrors, item } = validate(ksyosAccountSchema, filteredKsyosAccount);

            if (validationErrors.size > 0) {
                setErrors(validationErrors);
            } else {
                const url = `/api/practitioners/${ksyosAccount.id}/ksyosAccount`;
                const result = await abortableFetch.fetchJson(url, 'POST', item, [ValidationErrorCode.USERNAME_EXISTS]);
                if (result.errorCode) {
                    setErrors(errors.set('userName', result.errorCode));
                } else {
                    loadKsyosAccount();

                    const hasUpdatedUserName = originalKsyosAccount.userName !== item!.userName;
                    const hasUpdatedEmailAddress = originalKsyosAccount.emailAddress !== item!.emailAddress;

                    if (hasUpdatedUserName || hasUpdatedEmailAddress) {
                        updateActivationEmail(ksyosAccount);
                    }
                    setErrors(emptyValidationErrors);
                    setIsEditing(false);
                }
            }
            setShowConfirmationModal(false);
            setIsSaving(false);
        }
    }

    async function updateActivationEmail(account: IKsyosAccount) {
        if (account.activationEmailSentAt && !account.isActivated) {
            await abortableFetch.fetchJson(
                `/api/practitioners/${account.id}/activationEmail?isInitialActivation=true`,
                'POST'
            );
            showToast(ToastType.success, translateKey('profile.activationEmailResentTo', account.emailAddress));
        }
    }

    async function getEmploymentsInformation() {
        const now = DateTime.now();
        const employments = (
            await abortableFetch.fetchJson(
                `/api/user/employments/?practitionerId=${props.practitionerId}&onlyActives=false`,
                'GET'
            )
        ).json.items;
        if (employments.length === 0) {
            return;
        }
        const earliestWithNoEndDate = employments
            .sort((a: IEmploymentDates, b: IEmploymentDates) =>
                DateTime.from(a.startDate ?? now).isBefore(b.startDate ?? now)
            )
            .find(
                (employment: IEmploymentDates) =>
                    DateTime.from(employment.startDate ?? now).isBefore(now) && !employment.endDate
            );

        const earliestWithFutureStart = employments
            .filter((employment: IEmploymentDates) =>
                DateTime.from(employment.startDate ?? now).isAfter(DateTime.now())
            )
            .sort((a: IEmploymentDates, b: IEmploymentDates) =>
                DateTime.from(a.startDate ?? now).isBefore(b.startDate ?? now)
            )[0];

        const latestWithEndDate = employments
            .filter((employment: IEmploymentDates) => employment.endDate)
            .sort((a: IEmploymentDates, b: IEmploymentDates) =>
                DateTime.from(a.endDate ?? now).isAfter(b.endDate ?? now)
            )[0];

        if (earliestWithNoEndDate) {
            setEmploymentPeriod({ startDate: earliestWithNoEndDate.startDate, endDate: 'heden' });
        } else if (earliestWithFutureStart) {
            setEmploymentPeriod({ startDate: 'vanaf', endDate: earliestWithFutureStart.startDate });
        } else if (latestWithEndDate) {
            setEmploymentPeriod({ startDate: latestWithEndDate.startDate, endDate: latestWithEndDate.endDate });
        }
    }
}
